import { useState, useEffect } from 'react';
import supabase from '../supabase';
import { theme, font, inputStyle, labelStyle, pillButton, outlineButton, ghostButton } from './adminTheme';
import { useAdminLang } from './AdminLangContext';
import '../design-system.css';

// Communiqués — statements officiels affichés en overlay plein écran sur
// Home.js (table `announcements`). Un seul actif à la fois (enabled=true),
// mais chaque communiqué créé ici reste en historique pour la page
// publique /communiques.

// Style de "chip" pour le sélecteur de type — défini localement (plutôt
// qu'importé d'adminTheme.js) pour que ce fichier reste indépendant de la
// version d'adminTheme.js actuellement déployée.
const chipButtonStyle = {
    padding: '6px 12px',
    background: 'none',
    border: `1px solid ${theme.border}`,
    color: theme.text,
    borderRadius: 0,
    fontSize: '11px',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: font,
};

const TYPES = {
    fr: [
        { value: 'release', label: 'Diffusion immédiate' },
        { value: 'media', label: 'Communiqué de presse' },
        { value: 'global', label: 'Diffusion globale' },
        { value: 'update', label: 'Mise à jour' },
    ],
    en: [
        { value: 'release', label: 'Immediate release' },
        { value: 'media', label: 'Press release' },
        { value: 'global', label: 'Global release' },
        { value: 'update', label: 'Update' },
    ],
};

const T = {
    fr: {
        editTitle: 'Modifier le communiqué',
        newTitle: 'Nouveau communiqué',
        intro: "Un seul communiqué actif à la fois (affiché en overlay plein écran sur la page d'accueil) — tous les communiqués créés restent visibles dans l'historique public sur /communiques.",
        date: 'Date',
        location: 'Lieu',
        locationPh: 'Montréal, QC',
        type: 'Type',
        recipient: 'Destinataire (optionnel)',
        recipientPh: "Ex. À l'ensemble des créateurs winston.ie",
        greeting: "Formule d'appel (optionnel)",
        greetingPh: 'Ex. Mesdames, Messieurs,',
        titleField: 'Titre / Objet',
        titlePh: 'Ex. winston.ie entre dans une nouvelle ère.',
        text: 'Texte',
        textNote: "Les sauts de ligne, paragraphes et espaces sont conservés exactement tels que tu les écris ici — le texte s'affiche sur le site tel quel, aligné à gauche comme une vraie lettre.",
        closing: 'Formule de politesse (optionnel)',
        closingPh: 'Ex. Cordialement,',
        signatureName: 'Signature — nom ou équipe (optionnel)',
        signatureNamePh: "Ex. Andy Le — ou : L'équipe winston.ie",
        signatureTitle: 'Signature — titre (optionnel)',
        signatureTitlePh: 'Ex. Fondateur, winston.ie',
        pdf: 'Document PDF (optionnel)',
        pdfPh: 'https://...',
        uploading: 'Upload en cours...',
        linkView: 'Lien "Voir →" (optionnel)',
        saving: 'Sauvegarde...',
        update: 'Mettre à jour',
        create: 'Créer le communiqué',
        cancel: 'Annuler',
        history: 'Historique',
        loading: 'Chargement...',
        empty: 'Aucun communiqué pour le moment.',
        active: '● Actif',
        inactive: 'Inactif',
        edit: 'Modifier',
        deactivate: 'Désactiver',
        activate: 'Activer',
        delete: 'Supprimer',
        titleRequired: 'Le titre est requis.',
        uploadError: (msg) => `Erreur upload : ${msg}`,
        fileUploaded: 'Fichier uploadé',
        saveError: (msg) => `Erreur : ${msg}`,
        updated: 'Communiqué mis à jour',
        created: 'Communiqué créé (désactivé)',
        activatedMsg: 'Communiqué activé sur le site',
        deactivatedMsg: 'Communiqué désactivé',
        deletedMsg: 'Communiqué supprimé',
    },
    en: {
        editTitle: 'Edit the announcement',
        newTitle: 'New announcement',
        intro: 'Only one announcement is active at a time (shown as a full-screen overlay on the home page) — every announcement created here stays visible in the public archive at /communiques.',
        date: 'Date',
        location: 'Location',
        locationPh: 'Montreal, QC',
        type: 'Type',
        recipient: 'Recipient (optional)',
        recipientPh: 'e.g. To all winston.ie creators',
        greeting: 'Greeting (optional)',
        greetingPh: 'e.g. Dear all,',
        titleField: 'Title / Subject',
        titlePh: 'e.g. winston.ie enters a new era.',
        text: 'Text',
        textNote: "Line breaks, paragraphs and spacing are preserved exactly as you write them here — the text displays on the site as-is, left-aligned like a real letter.",
        closing: 'Closing (optional)',
        closingPh: 'e.g. Sincerely,',
        signatureName: 'Signature — name or team (optional)',
        signatureNamePh: 'e.g. Andy Le — or: The winston.ie team',
        signatureTitle: 'Signature — title (optional)',
        signatureTitlePh: 'e.g. Founder, winston.ie',
        pdf: 'PDF document (optional)',
        pdfPh: 'https://...',
        uploading: 'Uploading...',
        linkView: '"View →" link (optional)',
        saving: 'Saving...',
        update: 'Update',
        create: 'Create announcement',
        cancel: 'Cancel',
        history: 'History',
        loading: 'Loading...',
        empty: 'No announcements yet.',
        active: '● Active',
        inactive: 'Inactive',
        edit: 'Edit',
        deactivate: 'Deactivate',
        activate: 'Activate',
        delete: 'Delete',
        titleRequired: 'Title is required.',
        uploadError: (msg) => `Upload error: ${msg}`,
        fileUploaded: 'File uploaded',
        saveError: (msg) => `Error: ${msg}`,
        updated: 'Announcement updated',
        created: 'Announcement created (disabled)',
        activatedMsg: 'Announcement activated on the site',
        deactivatedMsg: 'Announcement deactivated',
        deletedMsg: 'Announcement deleted',
    },
};

const emptyForm = {
    date: new Date().toISOString().slice(0, 10),
    location: 'Montréal, QC',
    type: 'release',
    recipient: '',
    greeting: '',
    title: '',
    body: '',
    closing: 'Cordialement,',
    signature_name: '',
    signature_title: '',
    pdf_url: '',
    link_url: '',
};

function AdminAnnouncements() {
    const { lang } = useAdminLang();
    const t = T[lang] || T.fr;
    const TYPE_OPTIONS = TYPES[lang] || TYPES.fr;
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(emptyForm);
    const [editingId, setEditingId] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [statusMsg, setStatusMsg] = useState('');

    const fetchList = async () => {
        setLoading(true);
        const { data } = await supabase.from('announcements').select('*').order('date', { ascending: false });
        setList(data || []);
        setLoading(false);
    };

    useEffect(() => { fetchList(); }, []);

    const resetForm = () => {
        setForm(emptyForm);
        setEditingId(null);
    };

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        const fileName = `announcement-${Date.now()}.${file.name.split('.').pop()}`;
        const { error } = await supabase.storage.from('covers').upload(fileName, file, { upsert: true });
        if (error) setStatusMsg(t.uploadError(error.message));
        else {
            const { data: urlData } = supabase.storage.from('covers').getPublicUrl(fileName);
            setForm(f => ({ ...f, pdf_url: urlData.publicUrl }));
            setStatusMsg(t.fileUploaded);
        }
        setUploading(false);
    };

    const handleSave = async () => {
        if (!form.title.trim()) { setStatusMsg(t.titleRequired); return; }
        setSaving(true);
        const payload = {
            date: form.date,
            location: form.location || null,
            type: form.type,
            recipient: form.recipient || null,
            greeting: form.greeting || null,
            title: form.title,
            body: form.body || null,
            closing: form.closing || null,
            signature_name: form.signature_name || null,
            signature_title: form.signature_title || null,
            pdf_url: form.pdf_url || null,
            link_url: form.link_url || null,
        };

        let error;
        if (editingId) {
            ({ error } = await supabase.from('announcements').update(payload).eq('id', editingId));
        } else {
            ({ error } = await supabase.from('announcements').insert({ ...payload, enabled: false }));
        }

        if (error) setStatusMsg(t.saveError(error.message));
        else {
            setStatusMsg(editingId ? t.updated : t.created);
            resetForm();
            fetchList();
        }
        setSaving(false);
    };

    const handleActivate = async (id) => {
        setSaving(true);
        // Un seul communiqué actif à la fois — on désactive les autres.
        await supabase.from('announcements').update({ enabled: false }).neq('id', id);
        const { error } = await supabase.from('announcements').update({ enabled: true }).eq('id', id);
        if (error) setStatusMsg(t.saveError(error.message));
        else setStatusMsg(t.activatedMsg);
        setSaving(false);
        fetchList();
    };

    const handleDeactivate = async (id) => {
        setSaving(true);
        const { error } = await supabase.from('announcements').update({ enabled: false }).eq('id', id);
        if (!error) setStatusMsg(t.deactivatedMsg);
        setSaving(false);
        fetchList();
    };

    const handleEdit = (item) => {
        setEditingId(item.id);
        setForm({
            date: item.date || new Date().toISOString().slice(0, 10),
            location: item.location || 'Montréal, QC',
            type: item.type || 'release',
            recipient: item.recipient || '',
            greeting: item.greeting || '',
            title: item.title || '',
            body: item.body || '',
            closing: item.closing || 'Cordialement,',
            signature_name: item.signature_name || '',
            signature_title: item.signature_title || '',
            pdf_url: item.pdf_url || '',
            link_url: item.link_url || '',
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id) => {
        setSaving(true);
        const { error } = await supabase.from('announcements').delete().eq('id', id);
        if (error) setStatusMsg(t.saveError(error.message));
        else {
            setStatusMsg(t.deletedMsg);
            if (editingId === id) resetForm();
        }
        setSaving(false);
        fetchList();
    };

    const formatDate = (d) => {
        if (!d) return '—';
        const locale = lang === 'en' ? 'en-US' : 'fr-CA';
        return new Date(d + 'T00:00:00').toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
    };

    return (
        <div style={{ maxWidth: '640px' }}>
            <p style={{ ...labelStyle, marginBottom: '4px' }}>
                {editingId ? t.editTitle : t.newTitle}
            </p>
            <p style={{ fontFamily: font, fontSize: '11px', color: theme.textMuted, margin: '0 0 20px' }}>
                {t.intro}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '36px', paddingBottom: '28px', borderBottom: `1px solid ${theme.border}` }}>
                <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 140px' }}>
                        <label style={labelStyle}>{t.date}</label>
                        <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} style={inputStyle} />
                    </div>
                    <div style={{ flex: '1 1 160px' }}>
                        <label style={labelStyle}>{t.location}</label>
                        <input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} style={inputStyle} placeholder={t.locationPh} />
                    </div>
                </div>

                <div>
                    <label style={labelStyle}>{t.type}</label>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {TYPE_OPTIONS.map(opt => (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => setForm(f => ({ ...f, type: opt.value }))}
                                style={{
                                    ...chipButtonStyle,
                                    borderColor: form.type === opt.value ? theme.borderHover : theme.border,
                                    color: form.type === opt.value ? theme.text : theme.textMuted,
                                    fontWeight: form.type === opt.value ? 700 : 500,
                                }}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <label style={labelStyle}>{t.recipient}</label>
                    <input value={form.recipient} onChange={e => setForm(f => ({ ...f, recipient: e.target.value }))} style={inputStyle} placeholder={t.recipientPh} />
                </div>

                <div>
                    <label style={labelStyle}>{t.greeting}</label>
                    <input value={form.greeting} onChange={e => setForm(f => ({ ...f, greeting: e.target.value }))} style={inputStyle} placeholder={t.greetingPh} />
                </div>

                <div>
                    <label style={labelStyle}>{t.titleField}</label>
                    <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} style={inputStyle} placeholder={t.titlePh} />
                </div>

                <div>
                    <label style={labelStyle}>{t.text}</label>
                    <textarea
                        value={form.body}
                        onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                        rows={10}
                        style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.7, fontFamily: 'Helvetica Neue, Arial, sans-serif' }}
                    />
                    <p style={{ fontFamily: font, fontSize: '10.5px', color: theme.textFaint, margin: '6px 0 0' }}>
                        {t.textNote}
                    </p>
                </div>

                <div>
                    <label style={labelStyle}>{t.closing}</label>
                    <input value={form.closing} onChange={e => setForm(f => ({ ...f, closing: e.target.value }))} style={inputStyle} placeholder={t.closingPh} />
                </div>

                <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 220px' }}>
                        <label style={labelStyle}>{t.signatureName}</label>
                        <input value={form.signature_name} onChange={e => setForm(f => ({ ...f, signature_name: e.target.value }))} style={inputStyle} placeholder={t.signatureNamePh} />
                    </div>
                    <div style={{ flex: '1 1 220px' }}>
                        <label style={labelStyle}>{t.signatureTitle}</label>
                        <input value={form.signature_title} onChange={e => setForm(f => ({ ...f, signature_title: e.target.value }))} style={inputStyle} placeholder={t.signatureTitlePh} />
                    </div>
                </div>

                <div>
                    <label style={labelStyle}>{t.pdf}</label>
                    {form.pdf_url && (
                        <p style={{ fontFamily: font, fontSize: '11px', color: theme.textMuted, margin: '0 0 6px', wordBreak: 'break-all' }}>{form.pdf_url}</p>
                    )}
                    <input value={form.pdf_url} onChange={e => setForm(f => ({ ...f, pdf_url: e.target.value }))} style={{ ...inputStyle, marginBottom: '8px' }} placeholder={t.pdfPh} />
                    <input type="file" accept="application/pdf" onChange={handleUpload} disabled={uploading} style={{ fontSize: '11px', color: theme.textMuted, fontFamily: font }} />
                    {uploading && <p style={{ fontSize: '10px', color: theme.textMuted, margin: '4px 0 0' }}>{t.uploading}</p>}
                </div>

                <div>
                    <label style={labelStyle}>{t.linkView}</label>
                    <input value={form.link_url} onChange={e => setForm(f => ({ ...f, link_url: e.target.value }))} style={inputStyle} placeholder="/campaigns/2026" />
                </div>

                <div style={{ display: 'flex', gap: '10px', marginTop: '4px', alignItems: 'center' }}>
                    <button className="wlink" onClick={handleSave} disabled={saving} style={pillButton}>
                        {saving ? t.saving : editingId ? t.update : t.create}
                    </button>
                    {editingId && <button className="wlink" onClick={resetForm} style={outlineButton}>{t.cancel}</button>}
                </div>

                {statusMsg && <p style={{ fontSize: '11px', color: theme.textMuted, fontFamily: font, margin: 0 }}>{statusMsg}</p>}
            </div>

            <p style={{ ...labelStyle, marginBottom: '12px' }}>{t.history}</p>

            {loading ? (
                <p style={{ color: theme.textMuted, fontFamily: font, fontSize: '12px' }}>{t.loading}</p>
            ) : list.length === 0 ? (
                <p style={{ color: theme.textMuted, fontFamily: font, fontSize: '12px' }}>{t.empty}</p>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {list.map(item => (
                        <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', padding: '14px 0', borderBottom: `1px solid ${theme.border}` }}>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px', flexWrap: 'wrap' }}>
                                    <span style={{ fontFamily: font, fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: item.enabled ? theme.text : theme.textFaint }}>
                                        {item.enabled ? t.active : t.inactive}
                                    </span>
                                    <span style={{ fontFamily: font, fontSize: '11px', color: theme.textMuted }}>{formatDate(item.date)}</span>
                                    <span style={{ fontFamily: font, fontSize: '11px', color: theme.textMuted }}>· {TYPE_OPTIONS.find(opt => opt.value === item.type)?.label || item.type}</span>
                                </div>
                                <p style={{ fontFamily: font, fontSize: '13px', fontWeight: 700, color: theme.text, margin: 0 }}>{item.title}</p>
                            </div>
                            <div style={{ display: 'flex', gap: '12px', flexShrink: 0, alignItems: 'center' }}>
                                <button className="wlink" onClick={() => handleEdit(item)} style={ghostButton}>{t.edit}</button>
                                {item.enabled ? (
                                    <button className="wlink" onClick={() => handleDeactivate(item.id)} disabled={saving} style={ghostButton}>{t.deactivate}</button>
                                ) : (
                                    <button className="wlink" onClick={() => handleActivate(item.id)} disabled={saving} style={outlineButton}>{t.activate}</button>
                                )}
                                <button className="wlink" onClick={() => handleDelete(item.id)} disabled={saving} style={{ ...ghostButton, color: theme.danger }}>{t.delete}</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default AdminAnnouncements;